import React from 'react';
import FormField from '../shared/FormField';
import USStateSelect, { getStateName } from '../shared/USStateSelect';
import ZIPCodeInput from '../shared/ZIPCodeInput';
import EmailInput from '../shared/EmailInput';
import formDataStorage from '../../utils/formDataStorage';
import { User, Building2, Phone, Mail, MapPin, Users, ChevronDown } from 'lucide-react';

// 美国联系人信息表单
const USContactForm = ({ data, onChange, fieldErrors = {} }) => {

  // 从formDataStorage读取数据并确保数据同步
  React.useEffect(() => {
    const savedData = formDataStorage.loadFormData('us_contact');
    // 如果有保存的数据但当前data为空或部分字段缺失，则从数据库加载
    if (savedData && (!data || Object.keys(data).length === 0)) {
      Object.keys(savedData).forEach(key => {
        if (savedData[key] !== null && savedData[key] !== undefined) {
          onChange(key, savedData[key]);
        }
      });
    }
    console.log('USContactForm 加载的数据:', savedData)
  }, [data, onChange]);

  // 关系选项映射（与官方DS-160表单一致）
  const RELATIONSHIP_OPTIONS = [
    { value: 'R', label: 'R - RELATIVE (亲戚)' },
    { value: 'S', label: 'S - SPOUSE (配偶)' },
    { value: 'C', label: 'C - FRIEND (朋友)' },
    { value: 'B', label: 'B - BUSINESS ASSOCIATE (商业伙伴)' },
    { value: 'P', label: 'P - EMPLOYER (雇主)' },
    { value: 'H', label: 'H - SCHOOL OFFICIAL (学校官员)' },
    { value: 'O', label: 'O - OTHER (其他)' }
  ];

  // 更新联系人个人信息
  const updateContactPerson = (field, value) => {
    const currentContactPerson = data.contact_person || {};
    onChange('contact_person', { ...currentContactPerson, [field]: value });
  };

  // 更新联系人组织信息
  const updateContactOrganization = (field, value) => {
    const currentContactOrganization = data.contact_organization || {};
    onChange('contact_organization', { ...currentContactOrganization, [field]: value });
  };

  // 更新地址和电话信息
  const updateAddressAndPhone = (field, value) => {
    const currentAddressAndPhone = data.address_and_phone || {};
    const newAddressAndPhone = { ...currentAddressAndPhone, [field]: value };
    console.log('updateAddressAndPhone - 字段:', field, '值:', value);
    console.log('updateAddressAndPhone - 当前对象:', currentAddressAndPhone);
    console.log('updateAddressAndPhone - 新对象:', newAddressAndPhone);
    console.log('updateAddressAndPhone - 新对象中email_address_na:', newAddressAndPhone.email_address_na);
    onChange('address_and_phone', newAddressAndPhone);
  };

  // 获取关系显示名称
  const getRelationshipLabel = (code) => {
    const option = RELATIONSHIP_OPTIONS.find(opt => opt.value === code);
    return option ? option.label : code;
  };

  // 组织模式：当“个人不适用”为真时，按组织填写；此时不允许选择 R/S/C
  const usingOrganization = !!data.contact_person_na && !data.contact_orgnization_na;
  // 稳定化不允许的关系集合，避免在 hooks 依赖中每次渲染都变化
  const disallowedForOrg = React.useMemo(() => new Set(['R', 'S', 'C']), []);
  const filteredRelationshipOptions = usingOrganization
    ? RELATIONSHIP_OPTIONS.filter(opt => !disallowedForOrg.has(opt.value))
    : RELATIONSHIP_OPTIONS;

  // 若当前选择在组织模式下非法，则自动纠正为更合适的默认值
  React.useEffect(() => {
    if (usingOrganization && data.relationship && disallowedForOrg.has(data.relationship)) {
      // 加括号，避免 && 与 || 混用的优先级歧义
      const orgName = ((data.contact_organization && data.contact_organization.contact_organization_name) || '').toUpperCase();
      const suggested = /(UNIVERSITY|COLLEGE|SCHOOL|INSTITUTE)/.test(orgName) ? 'H' : 'B';
      onChange('relationship', suggested);
    }
  }, [usingOrganization, data.relationship, data.contact_organization, disallowedForOrg, onChange]);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">🇺🇸</span>
          美国联系人信息
        </h3>

        {/* 重要提示 */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>重要提示:</strong> 联系人个人信息和组织信息只能二选一，不能同时填写两种信息。
              </p>
            </div>
          </div>
        </div>

        {/* 联系人个人信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center">
              <User className="w-5 h-5 mr-2" />
              联系人个人信息
            </h4>
            <div className="flex items-center px-3 py-2 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
              <label className="flex items-center cursor-pointer">
                                 <input
                   type="checkbox"
                   checked={data.contact_person_na || false}
                   onChange={(e) => {
                     const checked = e.target.checked;
                     onChange('contact_person_na', checked);
                     if (checked) {
                       onChange('contact_person', { contact_surnames: '', contact_given_name: '' });
                       // 选择个人信息不适用时，自动启用组织信息
                       onChange('contact_orgnization_na', false);
                     } else {
                       // 选择个人信息时，自动设置组织信息为不适用
                       onChange('contact_orgnization_na', true);
                       onChange('contact_organization', { contact_organization_name: '' });
                     }
                   }}
                  className="mr-2 w-5 h-5 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span className="text-base font-medium text-gray-700">不适用</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="联系人姓氏"
              value={(data.contact_person && data.contact_person.contact_surnames) || ''}
              onChange={(e) => updateContactPerson('contact_surnames', e.target.value)}
              placeholder="SMITH"
              disabled={data.contact_person_na}
              required={!data.contact_person_na}
            />
            <FormField
              label="联系人名字"
              value={(data.contact_person && data.contact_person.contact_given_name) || ''}
              onChange={(e) => updateContactPerson('contact_given_name', e.target.value)}
              placeholder="JOHN"
              disabled={data.contact_person_na}
              required={!data.contact_person_na}
            />
          </div>

          {data.contact_person_na && (
            <div className="mt-4 p-3 bg-orange-100 rounded-lg border-l-4 border-orange-400">
              <p className="text-sm text-orange-700">
                <strong>注意：</strong> 已选择不适用，联系人个人信息字段将被禁用。
              </p>
            </div>
          )}
        </div>

        {/* 联系人组织信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center">
              <Building2 className="w-5 h-5 mr-2" />
              联系人所属组织
            </h4>
            <div className="flex items-center px-3 py-2 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
              <label className="flex items-center cursor-pointer">
                                 <input
                   type="checkbox"
                   checked={data.contact_orgnization_na || false}
                   onChange={(e) => {
                     const checked = e.target.checked;
                     onChange('contact_orgnization_na', checked);
                     if (checked) {
                       onChange('contact_organization', { contact_organization_name: '' });
                       // 选择组织信息不适用时，自动启用个人信息
                       onChange('contact_person_na', false);
                     } else {
                       // 选择组织信息时，自动设置个人信息为不适用
                       onChange('contact_person_na', true);
                       onChange('contact_person', { contact_surnames: '', contact_given_name: '' });
                     }
                   }}
                  className="mr-2 w-5 h-5 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span className="text-base font-medium text-gray-700">不适用</span>
              </label>
            </div>
          </div>

          <FormField
            label="组织名称"
            value={(data.contact_organization && data.contact_organization.contact_organization_name) || ''}
            onChange={(e) => updateContactOrganization('contact_organization_name', e.target.value)}
            placeholder="ARIZONA STATE UNIVERSITY"
            disabled={data.contact_orgnization_na}
            required={!data.contact_orgnization_na}
          />

          {data.contact_orgnization_na && (
            <div className="mt-4 p-3 bg-orange-100 rounded-lg border-l-4 border-orange-400">
              <p className="text-sm text-orange-700">
                <strong>注意：</strong> 已选择不适用，组织信息字段将被禁用。
              </p>
            </div>
          )}
        </div>

        {/* 与申请人的关系 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <Users className="w-5 h-5 mr-2" />
            与申请人的关系
          </h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              请选择关系类型 <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={data.relationship || ''}
                onChange={(e) => onChange('relationship', e.target.value)}
                className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer"
              >
                <option value="">请选择关系...</option>
                {filteredRelationshipOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>

            {data.relationship && (
              <div className="mt-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-400">
                <div className="text-sm font-medium text-green-800">
                  ✅ 已选择关系: {getRelationshipLabel(data.relationship)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 地址和电话信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <MapPin className="w-5 h-5 mr-2" />
            联系人地址和电话信息
          </h4>

          <div className="space-y-4">
            {/* 地址信息 */}
            <div className="space-y-4">
              <FormField
                label="地址第一行"
                value={(data.address_and_phone && data.address_and_phone.address_ln1) || ''}
                onChange={(e) => updateAddressAndPhone('address_ln1', e.target.value)}
                placeholder="123 Main Street"
                required
              />

              <FormField
                label="地址第二行（可选）"
                value={(data.address_and_phone && data.address_and_phone.address_ln2) || ''}
                onChange={(e) => updateAddressAndPhone('address_ln2', e.target.value)}
                placeholder="Apt 456 或 ARIZONA STATE UNIVERSITY"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  id="us_contact_city"
                  label="城市"
                  value={(data.address_and_phone && data.address_and_phone.city) || ''}
                  onChange={(e) => updateAddressAndPhone('city', e.target.value)}
                  placeholder="LOS ANGELES"
                  required
                />

                <div id="us_contact_state" />
                <USStateSelect
                  label="州"
                  value={(data.address_and_phone && data.address_and_phone.state) || ''}
                  onChange={(value) => updateAddressAndPhone('state', value)}
                  required
                />
              </div>

              <div id="us_contact_postal" />
              <ZIPCodeInput
                label="邮政编码"
                value={(data.address_and_phone && data.address_and_phone.postal_code) || ''}
                onChange={(value) => updateAddressAndPhone('postal_code', value)}
                placeholder="12345 或 12345-6789"
                required
              />
              {fieldErrors['#us_contact_state'] && (
                <div className="-mt-2 text-xs text-red-600">{fieldErrors['#us_contact_state']}</div>
              )}
              {fieldErrors['#us_contact_postal'] && (
                <div className="-mt-2 text-xs text-red-600">{fieldErrors['#us_contact_postal']}</div>
              )}
            </div>

            {/* 电话信息 */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400">
              <h5 className="text-md font-semibold text-gray-700 mb-4 flex items-center">
                <Phone className="w-4 h-4 mr-2" />
                电话信息
              </h5>
              <FormField
                label="电话号码"
                type="tel"
                value={(data.address_and_phone && data.address_and_phone.phone_number) || ''}
                onChange={(e) => updateAddressAndPhone('phone_number', e.target.value)}
                placeholder="114514"
                required
              />
            </div>

            {/* 邮箱信息 */}
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-400">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-md font-semibold text-gray-700 flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  邮箱信息
                </h5>
                <div className="flex items-center px-3 py-2 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={data.address_and_phone?.email_address_na || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const currentAddressAndPhone = data.address_and_phone || {};
                        const newAddressAndPhone = {
                          ...currentAddressAndPhone,
                          email_address_na: checked,
                          email_address: checked ? '' : currentAddressAndPhone.email_address
                        };
                        onChange('address_and_phone', newAddressAndPhone);
                      }}
                      className="mr-2 w-5 h-5 text-green-600 bg-white border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                    />
                    <span className="text-base font-medium text-gray-700">不适用</span>
                  </label>
                </div>
              </div>

              <div id="us_contact_email" />
              <EmailInput
                label="邮箱地址"
                value={data.address_and_phone?.email_address || ''}
                onChange={(value) => updateAddressAndPhone('email_address', value)}
                placeholder="test@example.com"
                disabled={data.address_and_phone?.email_address_na || false}
                required={!(data.address_and_phone?.email_address_na || false)}
              />
              {fieldErrors['#us_contact_email'] && (
                <div className="-mt-2 text-xs text-red-600">{fieldErrors['#us_contact_email']}</div>
              )}

              {data.address_and_phone?.email_address_na && (
                <div className="mt-3 p-2 bg-orange-100 rounded border-l-4 border-orange-400">
                  <p className="text-xs text-orange-700">
                    <strong>注意：</strong> 已选择不适用，邮箱地址字段将被禁用。
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 预览信息 */}
        {(data.contact_person || data.contact_organization || data.relationship || data.address_and_phone) && (
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400 shadow-sm">
            <h4 className="font-semibold text-blue-800 mb-2">信息预览</h4>
            <div className="text-sm text-blue-700 space-y-1">
              {/* 联系人信息预览 */}
              {data.contact_person_na ? (
                <p><strong>联系人:</strong> <span className="text-red-600">不适用</span></p>
              ) : data.contact_person && (data.contact_person.contact_surnames || data.contact_person.contact_given_name) ? (
                <p><strong>联系人:</strong> {data.contact_person.contact_surnames} {data.contact_person.contact_given_name}</p>
              ) : null}

              {/* 组织信息预览 */}
              {data.contact_orgnization_na ? (
                <p><strong>组织:</strong> <span className="text-red-600">不适用</span></p>
              ) : data.contact_organization && data.contact_organization.contact_organization_name ? (
                <p><strong>组织:</strong> {data.contact_organization.contact_organization_name}</p>
              ) : null}

              {/* 关系预览 */}
              {data.relationship && (
                <p><strong>关系:</strong> {getRelationshipLabel(data.relationship)}</p>
              )}

              {/* 地址预览 */}
              {data.address_and_phone && data.address_and_phone.city && (
                <p><strong>地址:</strong> {data.address_and_phone.city}, {data.address_and_phone.state ? getStateName(data.address_and_phone.state) : ''} {data.address_and_phone.postal_code}</p>
              )}

              {/* 电话预览 */}
              {data.address_and_phone && data.address_and_phone.phone_number && (
                <p><strong>电话:</strong> {data.address_and_phone.phone_number}</p>
              )}

              {/* 邮箱预览 */}
              {data.address_and_phone?.email_address_na ? (
                <p><strong>邮箱:</strong> <span className="text-red-600">不适用</span></p>
              ) : data.address_and_phone?.email_address ? (
                <p><strong>邮箱:</strong> {data.address_and_phone.email_address}</p>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default USContactForm;
